import {tv} from 'tailwind-variants'
import HgArticleHeroText, {
  type HgArticleHeroTextProps,
} from '~/design-system/hg/components/HgArticleHeroText'
import {type AuraColorToken} from '~/design-system/hg/tokens/colors'
import {getHexValueFromToken} from '~/design-system/hg/tokens/colors/utils'
import {cn} from '~/design-system/utils'

export type HgArticleHeroHighVerticalProps = {
  assetType: 'vertical'
  /** LG breakpoint asset */
  assetFourFiveRatio: React.ReactNode
  /** XS breakpoint asset */
  assetOneOneRatio?: React.ReactNode
  backgroundColorToken: AuraColorToken
  className?: string
} & HgArticleHeroTextProps

const hgArticleHeroHighVerticalVariants = tv({
  slots: {
    assetFourFiveRatio: 'aspect-h-5 aspect-w-4 lg:h-screen [&_img]:object-cover',
    assetOneOneRatio:
      'aspect-h-1 aspect-w-1 w-full lg:h-screen [&_img]:object-cover',
  },
  variants: {
    hasOneOneRatio: {
      true: {
        assetFourFiveRatio: 'hidden lg:block',
        assetOneOneRatio: 'lg:hidden',
      },
    },
  },
})

export default function HgArticleHeroHighVertical({
  assetFourFiveRatio,
  assetOneOneRatio,
  backgroundColorToken,
  className,
  ...articleHeroTextProps
}: HgArticleHeroHighVerticalProps) {
  const {
    assetFourFiveRatio: assetFourFiveRatioVariant,
    assetOneOneRatio: assetOneOneRatioVariant,
  } = hgArticleHeroHighVerticalVariants({
    hasOneOneRatio: !!assetOneOneRatio,
  })

  const backgroundColor = getHexValueFromToken(backgroundColorToken)

  return (
    <div
      className={cn(
        className,
        'relative items-center bg-[var(--background)] pt-blog-navbars-height lg:grid lg:grid-cols-2 lg:pt-0'
      )}
      style={{'--background': backgroundColor} as React.CSSProperties}
      data-nav-hidden
    >
      <div className="grid grid-cols-12 gap-x-16 p-s5 pb-s8 lg:block lg:px-s4 lg:pb-0 lg:pt-0">
        <HgArticleHeroText
          classNames={{
            subheading: 'sm:px-s8 lg:px-0',
            container: 'col-span-12 sm:col-span-8 sm:col-start-3',
          }}
          {...articleHeroTextProps}
        />
      </div>
      <div className="relative col-span-full lg:col-span-1 lg:h-screen lg:overflow-hidden">
        <div className={assetFourFiveRatioVariant()}>{assetFourFiveRatio}</div>
        {assetOneOneRatio && (
          <div className={assetOneOneRatioVariant()}>{assetOneOneRatio}</div>
        )}
        <div className="to-transparent absolute inset-0 bottom-[50%] bg-gradient-to-b from-[rgba(0,0,0,0.56)]" />
      </div>
    </div>
  )
}
